import useConversacaoStore from "../storeModules/useConversacaoStore";
import useConsorcioStore from "../storeModules/useConsorcioStore";
import useUserStore from "../storeModules/useUserStore";
import useBotControlStore from "../storeModules/useBotControlStore";
import usePaymentStore from "../storeModules/usePaymentStore";
import useInsuranceStore from "../storeModules/useInsuranceStore";
import { registrarStep, buildConfigMetadata } from "../api/requestsFunctions/conversacao";
import getStepBotOptions from "./getStepBotOptions";
import { OPENING_BLOCK_STEP } from "./openingBlockConstants";
import { getStructuredItemsForStep } from "./buildStepStructuredItems";
import { buildPreludeBlockItems } from "./stepMessageBlocks";

const USER_STEP_METADATA_FIELDS = {
  pergunta_nome: "user_name",
  pergunta_email: "user_email",
  pergunta_celular: "user_phone",
  cpf_cnpj: "user_document",
  cep: "user_cep",
  nome_completo: "user_full_name",
  nascimento_fundacao: "nascimento_fundacao",
  alterar_nome_completo: "user_full_name",
  alterar_email: "user_email",
  alterar_cpf_cnpj: "user_document",
  alterar_celular: "user_phone",
  alterar_cep: "user_cep",
  alterar_nascimento: "nascimento_fundacao",
};

const PROPOSAL_METADATA_STEPS = new Set(["proposta_gerada"]);

const CUPOM_METADATA_STEPS = new Set([
  "cupom_aplicado",
  "novo_valor_com_cupom",
  "mensagem_pagamento_cupom",
]);

const INSURANCE_PLAN_METADATA_STEPS = new Set([
  "escolha_seguro",
  "termos_seguro",
]);

const getSelectedPlanoField = (selectedPlano, ...keys) => {
  for (const key of keys) {
    const value = selectedPlano?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
};

const getParcelaMensalForMetadata = () => {
  const { selectedPlano } = useConsorcioStore.getState();
  const { isInsuranceHired, valorPremio } = useInsuranceStore.getState();

  const baseMonthly = getSelectedPlanoField(
    selectedPlano,
    "planoValorParcela",
    "PlanoValorParcela",
  );

  if (!isInsuranceHired) return baseMonthly;

  const monthlyWithInsurance = getSelectedPlanoField(
    selectedPlano,
    "valorParcelaComSeguro",
  );
  if (monthlyWithInsurance !== undefined) return monthlyWithInsurance;

  if (baseMonthly !== undefined && valorPremio) {
    return baseMonthly + valorPremio;
  }

  return baseMonthly;
};

const getValorParcelaPrimeiroPagtoSemCupom = () => {
  const { selectedPlano } = useConsorcioStore.getState();
  const { isInsuranceHired } = useInsuranceStore.getState();

  const baseFirst = getSelectedPlanoField(
    selectedPlano,
    "planoValorPagamento",
    "PlanoValorPagamento",
  );

  if (!isInsuranceHired) return baseFirst;

  return (
    getSelectedPlanoField(
      selectedPlano,
      "valorParcelaPrimeiroPagtoComSeguro",
    ) ?? getParcelaMensalForMetadata()
  );
};

const getValorParcelaPrimeiroPagtoComCupom = () => {
  const { cupomData } = useUserStore.getState();
  const { couponFirstParcel } = usePaymentStore.getState();
  const { valorPremio, isInsuranceHired } = useInsuranceStore.getState();

  const valorDescontado = couponFirstParcel || cupomData?.valorDescontado;
  if (!valorDescontado) return undefined;

  return isInsuranceHired ? valorDescontado + valorPremio : valorDescontado;
};

const buildInsurancePlanMetadata = (stepName) => {
  if (!INSURANCE_PLAN_METADATA_STEPS.has(stepName)) return {};

  const { isInsuranceHired } = useInsuranceStore.getState();
  if (!isInsuranceHired) return {};

  const parcela = getParcelaMensalForMetadata();
  const valorParcelaPrimeiroPagto = getValorParcelaPrimeiroPagtoSemCupom();

  const metadata = {};
  if (parcela !== undefined) metadata.parcela = parcela;
  if (valorParcelaPrimeiroPagto !== undefined) {
    metadata.valorParcelaPrimeiroPagto = valorParcelaPrimeiroPagto;
  }

  return metadata;
};

const buildCupomStepMetadata = (stepName) => {
  if (!CUPOM_METADATA_STEPS.has(stepName)) return {};

  const valorParcelaPrimeiroPagto = getValorParcelaPrimeiroPagtoComCupom();
  if (valorParcelaPrimeiroPagto === undefined) return {};

  const parcela = getParcelaMensalForMetadata();

  const metadata = {
    cupom: true,
    valorParcelaPrimeiroPagto,
  };

  if (parcela !== undefined) {
    metadata.parcela = parcela;
  }

  return metadata;
};

const buildProposalStepMetadata = (stepName) => {
  if (!PROPOSAL_METADATA_STEPS.has(stepName)) return {};

  const { contrato } = useConsorcioStore.getState().fetchedProposta ?? {};
  if (!contrato) return {};

  return { contrato: String(contrato) };
};

const buildUserStepMetadata = (stepName, userMessage, userVariables) => {
  const stepMetadata = {};

  if (userVariables && typeof userVariables === "object") {
    Object.assign(stepMetadata, userVariables);
  }

  const field = USER_STEP_METADATA_FIELDS[stepName];
  if (field && userMessage) {
    stepMetadata[field] = String(userMessage).trim();
  }

  return stepMetadata;
};

const buildStepMetadata = ({
  stepName,
  userMessage,
  userVariables,
  extraMetadata = {},
}) => {
  const metadata = {
    ...(stepName === OPENING_BLOCK_STEP ? buildConfigMetadata() : {}),
    ...buildProposalStepMetadata(stepName),
    ...buildInsurancePlanMetadata(stepName),
    ...buildCupomStepMetadata(stepName),
    ...buildUserStepMetadata(stepName, userMessage, userVariables),
    ...extraMetadata,
  };

  const entries = Object.entries(metadata).filter(
    ([, value]) => value !== null && value !== undefined && value !== "",
  );

  if (!entries.length) return undefined;

  return Object.fromEntries(entries);
};

const buildContent = (
  stepName,
  botMessage,
  botOptions = [],
  userMessage = null,
  { userOnly = false, preludeBlock = [], userVariables = null } = {}
) => {
  const content = [];

  if (!userOnly) {
    const structuredItems = getStructuredItemsForStep(stepName) ?? [];
    const usesOpeningBlock =
      stepName === OPENING_BLOCK_STEP && structuredItems.length > 0;
    const botItems = [...buildPreludeBlockItems(preludeBlock)];

    if (!usesOpeningBlock) {
      const botTexts = Array.isArray(botMessage) ? botMessage : [botMessage];
      botTexts
        .filter((m) => m !== null && m !== undefined && m !== "")
        .forEach((m) => {
          botItems.push({ type: "text", message: String(m) });
        });
    }

    structuredItems.forEach((item) => {
      botItems.push(item);
    });

    if (botOptions.length > 0 && !usesOpeningBlock) {
      botItems.push({
        type: "button",
        message: botOptions.map((opt) => ({ "tx-button": String(opt) })),
      });
    }

    if (botItems.length > 0) {
      content.push({ sender: "bot", step: stepName, items: botItems });
    }
  }

  if (userMessage !== null && userMessage !== undefined && userMessage !== "") {
    const userItems = [{ type: "text", message: String(userMessage) }];

    if (
      userVariables &&
      typeof userVariables === "object" &&
      Object.keys(userVariables).length > 0
    ) {
      userItems.push({ type: "variables", message: { ...userVariables } });
    }

    content.push({
      sender: "user",
      step: stepName,
      items: userItems,
    });
  }

  return content;
};

const resolveBotOptions = (stepName, explicitBotOptions, userOnly) => {
  if (userOnly) return [];

  if (explicitBotOptions !== undefined) return explicitBotOptions;

  const { lastRenderedStep } = useBotControlStore.getState();
  if (lastRenderedStep.name === stepName && lastRenderedStep.botOptions?.length) {
    return lastRenderedStep.botOptions;
  }

  try {
    const steps = require("../screens/bot/stepModules/steps").default;
    const step = steps.find((entry) => entry.name === stepName);
    return step ? getStepBotOptions(step) : [];
  } catch {
    return lastRenderedStep.botOptions ?? [];
  }
};

const trackConversacaoStep = ({
  stepName,
  botMessage: explicitBotMessage,
  botOptions: explicitBotOptions,
  userMessage = null,
  userVariables = null,
  metadata = {},
  userOnly = false,
  preludeBlock = [],
}) => {
  const { lastRenderedStep } = useBotControlStore.getState();

  const botMessage = userOnly
    ? ""
    : explicitBotMessage !== undefined
      ? explicitBotMessage
      : lastRenderedStep.botMessage;

  const botOptions = resolveBotOptions(stepName, explicitBotOptions, userOnly);

  const storeConversacaoId = useConversacaoStore.getState().conversacaoId;
  const localConversacaoId = localStorage.getItem("mycon_conversacao_id");
  const conversacaoId = storeConversacaoId || localConversacaoId || "";

  const { LeadID } = useUserStore.getState().lead;
  const localLeadId = localStorage.getItem("mycon_lead_id");
  const leadid = LeadID || localLeadId || "";

  const stepMetadata = buildStepMetadata({
    stepName,
    userMessage,
    userVariables,
    extraMetadata: metadata,
  });

  const content = buildContent(stepName, botMessage, botOptions ?? [], userMessage, {
    userOnly,
    preludeBlock,
    userVariables,
  });

  const payload = {
    chat_id: conversacaoId,
    leadid,
    step: stepName,
    content,
  };

  if (stepMetadata) {
    payload.metadata = stepMetadata;
  }

  if (!conversacaoId) return Promise.resolve();

  return registrarStep(conversacaoId, payload);
};

export {
  buildContent,
  buildStepMetadata,
  getParcelaMensalForMetadata,
  getValorParcelaPrimeiroPagtoComCupom,
  USER_STEP_METADATA_FIELDS,
};
export default trackConversacaoStep;
